import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import SlidingMenu, { SlidingMenuOption } from '../general-components/sliding-menu';
import ContentFormProvider from "../../contexts/generated-content/content-form-context";
import ContentGeneratorHandler from "./settings/content-generator-handler";
import AudioSettings from './audio-settings';
import { GreenroomState } from "../../stores/types/types";
import { MEDIA_SETTINGS_TABS } from "../../stores/types/session"
import { TABS } from "../../stores/types/tabs";
import ThemeTab from "./stylepacks/themes/theme-tab";
import { setEditingTheme } from "../../stores/actions/themes";
import { ROOM_STATES } from '../../stores/reducers/settings';
import { setActiveSubTab } from '../../stores/actions/tabs';
import useEpidemicSound from '../../hooks/use-epidemic-sound';
import useMediaItemMemos from '../../hooks/use-media-memos';
import { isEpidemicSoundMedia } from '../../utils/media';

const menuOptions: SlidingMenuOption[] = [
	{ label: 'Content', data: { value: MEDIA_SETTINGS_TABS.CONTENT } },
	{ label: 'Style', data: { value: MEDIA_SETTINGS_TABS.THEME } },
	{ label: 'Audio', data: { value: MEDIA_SETTINGS_TABS.AUDIO } },
];


interface MenuProps {
	currentTab: SlidingMenuOption;
	menuCallback: (o: SlidingMenuOption) => void;
	mediaProvider: string | null | undefined;
	hideAudioTab: boolean;
}
function MediaSettingsSlidingMenu(props: MenuProps) {
	const roomState = useSelector((s: GreenroomState) => s.MeetingReducer.roomState);

	const filteredMenuOptions = React.useMemo(() => {
		let options: SlidingMenuOption[];
		if (props.mediaProvider === 'epidemic sound') {
			// has to be epidemic, only shows audio tab
			options = menuOptions.filter((o) => o.data.value === MEDIA_SETTINGS_TABS.AUDIO);
		} else if (props.mediaProvider === 'capsule') {
			// its capsule, so shows all tabs
			if (roomState !== ROOM_STATES.EDITING) {
				// unless outside of showbuilder. Then it hides audio (old functionality, unsure why)
				options = menuOptions.filter((o) => o.data.value !== MEDIA_SETTINGS_TABS.AUDIO);
			} else {
				options = menuOptions;
			}
		} else {
			// no known provider, no known tabs, no sliding menu
			options = [];
		}
		// with Epidemic Sound off there is no editor for an Epidemic-sourced track
		return props.hideAudioTab
			? options.filter((o) => o.data.value !== MEDIA_SETTINGS_TABS.AUDIO)
			: options;
	}, [roomState, props.mediaProvider, props.hideAudioTab]);

	return filteredMenuOptions.length > 1 ? <SlidingMenu
		options={filteredMenuOptions}
		selectedOption={props.currentTab}
		callback={props.menuCallback}
		className="padding-left-small position-relative width-calc-full"
		fullWidthDivider={true}
		dividerClassname='lemonade'
	/> : null;
}

interface Props {
	isForShotEditorModal?: boolean;
}

export default function MediaSettings(props: Props) {
	const { isForShotEditorModal = false } = props;

	const activeSubTabs = useSelector((s: GreenroomState) => s.TabsReducer.activeSubTabs);
	const selectedMediaItem = useSelector((s: GreenroomState) => s.GeneratedContentReducer.selectedMediaItem);
	const { isEpidemicSoundBlocked } = useEpidemicSound();
	// the Audio sub-tab edits this item, not selectedMediaItem: navigating here from
	// a style pack selects the style-pack item while the audio feed stays Epidemic
	const { currentAudioMediaItem } = useMediaItemMemos();
	const hideAudioTab = isEpidemicSoundBlocked(currentAudioMediaItem);

	const dispatch = useDispatch();

	const initialTab = React.useMemo(() => {
		const found = menuOptions.find((x) => x.data.value === activeSubTabs[TABS.GENERATE_MEDIA_CONTENT]);
		// a persisted sub-tab of AUDIO from before the flag was turned off shouldn't
		// resolve; non-epidemic audio is unaffected
		if (found?.data.value === MEDIA_SETTINGS_TABS.AUDIO && hideAudioTab) {
			return undefined;
		} else {
			return found;
		}
	}, [activeSubTabs[TABS.GENERATE_MEDIA_CONTENT], hideAudioTab]);

	useEffect(() => {
		setCurrentTab(initialTab ?? menuOptions[0]);
		dispatch(setEditingTheme(null, false));
	}, [initialTab, selectedMediaItem?.id]);

	const [currentTab, setCurrentTab] = useState(initialTab ?? menuOptions[0]);

	const menuCallback = useCallback((o: SlidingMenuOption) => {
		setCurrentTab(o);
		dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, o.data.value));
	}, [])

	// Audio is the epidemic provider's only tab, so with the feature off there is
	// nothing to show - falling back to Content would render the generator form
	// against a track it was never meant for
	if (hideAudioTab && isEpidemicSoundMedia(selectedMediaItem)) {
		return null;
	} else {
		return (<>
			<MediaSettingsSlidingMenu
				currentTab={currentTab}
				menuCallback={menuCallback}
				mediaProvider={selectedMediaItem?.provider}
				hideAudioTab={hideAudioTab}
			/>
			<ContentFormProvider isForShotEditorModal={isForShotEditorModal}>
				{currentTab?.data?.value === MEDIA_SETTINGS_TABS.CONTENT && (
						<ContentGeneratorHandler fieldTypes={['image', 'string', 'duration']}/>
					)}
				{currentTab?.data?.value === MEDIA_SETTINGS_TABS.THEME && (
						<ThemeTab isForShotEditorModal={isForShotEditorModal}/>
				)}
				{currentTab?.data?.value === MEDIA_SETTINGS_TABS.AUDIO && !hideAudioTab && (
					<AudioSettings />
				)}
			</ContentFormProvider>
		</>);
	}
}
